import React, { useEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from "react"
import { AppButton } from "@/components/button/AppButton"
import { checkBeamioAccountAPI, createRecover } from "@/services/beamio"
import {
	Eye,
	EyeOff,
	AlertTriangle,
	Check,
	Loader,
	KeyRound,
	RefreshCw,
	Fingerprint,
	Shield,
} from "lucide-react"
import { ACTIVATING_STEPS } from "./RecoveryQRScreen"

const BEAMIO_TERMS_URL = "https://beamio.app/terms"
const BEAMIO_PRIVACY_URL = "https://beamio.app/privacy"

function CreateIdentityDecorativeBg() {
	return (
		<>
			<div
				className="pointer-events-none fixed top-0 right-0 -z-10 h-[33vmin] w-[33vmin] translate-x-1/3 -translate-y-1/3 rounded-full bg-[#004bc3]/[0.06] blur-[120px]"
				aria-hidden
			/>
			<div
				className="pointer-events-none fixed bottom-0 left-0 -z-10 h-[50vmin] w-[50vmin] -translate-x-1/4 translate-y-1/4 rounded-full bg-[#465c99]/[0.06] blur-[160px]"
				aria-hidden
			/>
		</>
	)
}

/** 单页表单：双段进度均视为本步完成（createBeamioTag.html 一栏式） */
function CreateIdentityHeader() {
	return (
		<header className="sticky top-0 z-50 flex shrink-0 items-center justify-between border-b border-[#e2e2e7]/60 bg-[#f3f3f8]/90 px-5 py-3 backdrop-blur-xl md:px-6 pt-[max(0.5rem,env(safe-area-inset-top))]">
			<div className="flex items-center gap-2">
				<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#004bc3] to-[#1562f0] shadow-sm">
					<Fingerprint className="h-4 w-4 text-white" strokeWidth={2.5} aria-hidden />
				</div>
				<span className="text-xl font-bold tracking-tighter text-[#1a1c1f]">Verra</span>
			</div>
			<div className="flex items-center gap-1">
				<div className="h-1.5 w-8 rounded-full bg-[#004bc3]" />
				<div className="h-1.5 w-8 rounded-full bg-[#004bc3]" />
			</div>
		</header>
	)
}

const CREATING_STEPS = [
	{ id: 0, title: "Generating Secure ID", desc: "Creating cryptographic keys", icon: KeyRound },
	{ id: 1, title: "Finalizing Terminal", desc: "Preparing user interface", icon: RefreshCw },
] as const
const STEP_DURATION_MS = 2000
const ACTIVATING_STEP_DURATION_MS = 5000

export type CreateUsernamePinScreenRef = { goBack: () => boolean }

const CREATE_RECOVER_START_DELAY_MS = 300

const CreateUsernamePinScreen = forwardRef<
	CreateUsernamePinScreenRef,
	{
		close: (val: { qrDataUrl: string; pin: string; passcode: string; temp: any; beamioTag: string }) => void
		isRedeemFlow?: boolean
		/** 创建钱包 loading 时通知父级（用于隐藏顶栏、整页垂直居中，避免光晕被裁切） */
		onCreatingWalletChange?: (creating: boolean) => void
	}
>(function CreateUsernamePinScreen({ close, isRedeemFlow = false, onCreatingWalletChange }, ref) {
	const [beamioName, setBeamioName] = useState("")
	const [password, setPassword] = useState("")
	const [showPassword, setShowPassword] = useState(false)
	const lastCheckedRef = useRef("")
	const passwordInputRef = useRef<HTMLInputElement>(null)
	const [tagStatus, setTagStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle")
	const [tagError, setTagError] = useState("")
	const [loading, setLoading] = useState(false)
	const [creatingStep, setCreatingStep] = useState(0)

	const steps = isRedeemFlow ? ACTIVATING_STEPS : CREATING_STEPS
	const stepDuration = isRedeemFlow ? ACTIVATING_STEP_DURATION_MS : STEP_DURATION_MS

	const localValidateTag = (raw: string) => {
		const trimmed = raw.trim().replace(/^@+/, "")
		if (!trimmed) return { ok: false, v: "", msg: "Please enter a BeamioTag" }
		if (!/^[a-zA-Z0-9_\.]{3,20}$/.test(trimmed)) {
			return { ok: false, v: trimmed, msg: "Use 3–20 letters, numbers or dots" }
		}
		return { ok: true, v: trimmed, msg: "" }
	}

	const validateAndCheckTag = async () => {
		if (tagStatus === "checking") return false

		const { ok, v, msg } = localValidateTag(beamioName)
		setTagError("")

		if (!ok) {
			if (v.length > 0) {
				setTagStatus("invalid")
				setTagError(msg)
			} else {
				setTagStatus("idle")
			}
			return false
		}

		if (v === lastCheckedRef.current && tagStatus === "valid") return true
		lastCheckedRef.current = v

		setTagStatus("checking")
		try {
			const available = await checkBeamioAccountAPI(v)
			if (!available) {
				setTagStatus("invalid")
				setTagError(`@${v} is already taken`)
				return false
			}
			setTagStatus("valid")
			setTagError("")
			return true
		} catch {
			setTagStatus("invalid")
			setTagError("Network error. Try again.")
			return false
		}
	}

	const tagChecking = tagStatus === "checking"
	const tagValid = tagStatus === "valid"

	useEffect(() => {
		const trimmed = beamioName.trim().replace(/^@+/, "")
		if (trimmed.length <= 2) return
		if (trimmed === lastCheckedRef.current && tagStatus === "valid") return

		const t = setTimeout(() => {
			void validateAndCheckTag()
		}, 3000)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 仅 beamioName / tagStatus 重置 debounce
	}, [beamioName, tagStatus])

	const strength = useMemo(() => {
		const p = password
		if (!p.length) return { filled: 0, percent: 0, label: "Enter a password" as const }
		let score = 0
		if (p.length >= 6) score++
		if (p.length >= 8) score++
		if (/[0-9]/.test(p)) score++
		if (/[a-zA-Z]/.test(p)) score++
		const filled = Math.min(4, score)
		const percent = filled * 25
		const label =
			filled <= 1
				? ("Weak" as const)
				: filled === 2
					? ("Fair" as const)
					: filled === 3
						? ("Good" as const)
						: ("Strong security" as const)
		return { filled, percent, label }
	}, [password])

	const pwdOk = password.trim().length >= 6
	const canSubmit = tagValid && pwdOk && !loading && !tagChecking

	useEffect(() => {
		onCreatingWalletChange?.(loading)
	}, [loading, onCreatingWalletChange])

	useEffect(() => {
		if (!loading) {
			setCreatingStep(0)
			return
		}
		const advance = () => setCreatingStep((prev) => Math.min(prev + 1, steps.length - 1))
		const timers: ReturnType<typeof setTimeout>[] = []
		// 与 CREATE_RECOVER_START_DELAY_MS 对齐：先留出首帧与 Step1，再与加密运算并行计时
		const lead = CREATE_RECOVER_START_DELAY_MS
		for (let i = 1; i < steps.length; i++) {
			timers.push(setTimeout(advance, lead + i * stepDuration))
		}
		return () => timers.forEach((t) => clearTimeout(t))
	}, [loading, isRedeemFlow, steps.length, stepDuration])

	useImperativeHandle(
		ref,
		() => ({
			goBack: () => false,
		}),
		[]
	)

	const handleCreateWallet = async (pwd: string) => {
		const trimmedTag = (beamioName || "").trim().replace(/^@+/, "")
		if (!trimmedTag || pwd.length < 6) return

		setLoading(true)
		// 先让浏览器提交 loading UI 并跑几步动画，再进入会长时间占用主线程的加密运算
		await new Promise<void>((resolve) => {
			window.setTimeout(resolve, CREATE_RECOVER_START_DELAY_MS)
		})

		let kks: Awaited<ReturnType<typeof createRecover>> = null
		try {
			kks = await createRecover(trimmedTag, pwd)
		} finally {
			setLoading(false)
		}

		if (!kks) return

		close({
			qrDataUrl: kks.qrCode,
			pin: pwd,
			passcode: kks.recoverCode,
			temp: kks.temp,
			beamioTag: trimmedTag,
		})
	}

	const onSubmitPress = async () => {
		if (!canSubmit) return
		const ok = await validateAndCheckTag()
		if (!ok) return
		await handleCreateWallet(password.trim())
	}

	if (loading) {
		return (
			<div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-x-hidden overflow-y-auto bg-white px-6 py-12 dark:bg-white">
				<div className="flex w-full max-w-sm flex-col items-center justify-center">
					<div className="relative mb-10 py-8">
						<div className="flex h-20 w-20 items-center justify-center rounded-[28px] bg-gradient-to-br from-[#1562f0] to-[#0e4cbb] shadow-[0_14px_40px_rgba(21,98,240,0.38)]">
							<Loader className="h-9 w-9 animate-spin text-white" strokeWidth={2.5} />
						</div>
						<div
							className="pointer-events-none absolute -inset-4 animate-pulse rounded-[40px] bg-[#1562f0] opacity-[0.12] blur-xl"
							aria-hidden
						/>
					</div>
					<div className="w-full space-y-6">
					{steps.map((s, idx) => {
						const isCompleted = idx < creatingStep
						const isActive = idx === creatingStep
						const Icon = s.icon
						return (
							<div key={s.id} className="flex items-start gap-4">
								<div
									className={[
										"flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors",
										isCompleted && "bg-[#0e4cbb]",
										isActive && "bg-[#1562f0]",
										!isCompleted && !isActive && "bg-slate-200",
									]
										.filter(Boolean)
										.join(" ")}
								>
									{isCompleted ? (
										<Check className="h-5 w-5 text-white" strokeWidth={2.5} />
									) : isActive ? (
										<Icon className="h-5 w-5 text-white" strokeWidth={2.5} />
									) : (
										<Icon className="h-5 w-5 text-slate-400" strokeWidth={2.5} />
									)}
								</div>
								<div className="min-w-0 flex-1 pt-0.5">
									<p
										className={[
											"text-[15px] font-semibold transition-colors",
											isActive && "font-bold text-[#1562f0]",
											isCompleted && "text-slate-700",
											!isCompleted && !isActive && "text-slate-400",
										]
											.filter(Boolean)
											.join(" ")}
									>
										{s.title}
									</p>
									<p
										className={[
											"mt-0.5 text-sm transition-colors",
											isActive && "text-slate-700",
											isCompleted && "text-slate-500",
											!isCompleted && !isActive && "text-slate-400",
										]
											.filter(Boolean)
											.join(" ")}
									>
										{s.desc}
									</p>
								</div>
							</div>
						)
					})}
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f3f3f8]">
			<CreateIdentityDecorativeBg />
			<CreateIdentityHeader />
			<main className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 py-3 [@media(max-height:700px)]:px-4 [@media(max-height:700px)]:py-2">
				<div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
					<div className="shrink-0 text-center">
						<h1 className="mb-1 text-2xl font-extrabold tracking-tight text-[#1a1c1f] md:text-3xl [@media(max-height:700px)]:text-[22px]">
							Create your Verra ID
						</h1>
						<p className="line-clamp-2 text-sm font-medium leading-snug text-[#424655] [@media(max-height:700px)]:text-[13px]">
							Your unique identity in the Verra network. Use it to connect with friends and local brands.
						</p>
					</div>

					<div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden [@media(max-height:700px)]:mt-2 [@media(max-height:700px)]:gap-2">
						<div className="min-h-0 space-y-3 [@media(max-height:700px)]:space-y-2">
						<div className="space-y-1.5">
							<label htmlFor="create-beamio-tag-input" className="block px-4 text-xs font-bold uppercase tracking-widest text-[#424655]">
								BeamioTag
							</label>
							<div className="relative">
								<div className="pointer-events-none absolute inset-y-0 left-5 flex items-center">
									<span className="text-lg font-bold text-[#004bc3]">@</span>
								</div>
								<input
									id="create-beamio-tag-input"
									readOnly={loading || tagChecking}
									autoCapitalize="none"
									autoCorrect="off"
									enterKeyHint="next"
									autoComplete="username"
									className={[
										"w-full rounded-2xl border-none bg-[#e2e2e7] py-3.5 pl-12 pr-14 text-base font-semibold text-[#1a1c1f] outline-none transition-all placeholder:text-[#737687]/50 [@media(max-height:700px)]:py-3",
										"focus:ring-2 focus:ring-[#004bc3]/20",
										"disabled:opacity-70",
										tagStatus === "invalid" ? "ring-2 ring-orange-400/80 focus:ring-orange-400/30" : "",
									].join(" ")}
									value={beamioName}
									placeholder="username"
									onChange={(e) => {
										if (tagChecking) return
										const next = e.currentTarget.value.replace(/@/g, "")
										setBeamioName(next)
										setTagStatus("idle")
										setTagError("")
									}}
									onBlur={async () => {
										const trimmed = beamioName.trim().replace(/^@+/, "")
										if (trimmed.length < 3) return
										const ok = await validateAndCheckTag()
										if (ok) passwordInputRef.current?.focus()
									}}
									onKeyDown={async (e) => {
										if (e.key === "Enter") {
											e.preventDefault()
											e.stopPropagation()
											const ok = await validateAndCheckTag()
											if (ok) passwordInputRef.current?.focus()
										}
									}}
								/>
								<div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
									{tagChecking && (
										<div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80">
											<div className="h-4 w-4 animate-spin rounded-full border-2 border-[#737687] border-t-[#004bc3]" />
										</div>
									)}
									{tagValid && !tagChecking && (
										<div className="flex items-center justify-center rounded-full bg-emerald-500 p-0.5">
											<Check className="h-4 w-4 text-white" strokeWidth={3} aria-hidden />
										</div>
									)}
								</div>
							</div>
							{tagStatus === "invalid" ? (
								<div className="flex items-center gap-2 px-4 pt-1 text-orange-600">
									<AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
									<span className="text-[13px] font-semibold leading-snug">{tagError}</span>
								</div>
							) : tagValid ? (
								<p className="px-4 pt-1 text-[13px] font-medium text-emerald-600">This tag is available</p>
							) : (
								<p className="px-4 pt-1 text-[13px] font-medium text-[#424655]">Permanent. Cannot be changed later.</p>
							)}
						</div>

						<div className="space-y-3 [@media(max-height:700px)]:space-y-2">
							<div className="space-y-1.5">
								<label htmlFor="create-wallet-password" className="block px-4 text-xs font-bold uppercase tracking-widest text-[#424655]">
									Secure Password
								</label>
								<div className="relative">
									<input
										ref={passwordInputRef}
										id="create-wallet-password"
										readOnly={loading}
										type={showPassword ? "text" : "password"}
										autoComplete="new-password"
										enterKeyHint="done"
										className="w-full rounded-2xl border-none bg-[#e2e2e7] py-3.5 pl-5 pr-14 text-base font-semibold text-[#1a1c1f] outline-none transition-all placeholder:text-[#737687]/50 focus:ring-2 focus:ring-[#004bc3]/20 disabled:opacity-70 [@media(max-height:700px)]:py-3"
										value={password}
										placeholder="••••••••••••"
										onChange={(e) => setPassword(e.currentTarget.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter" && canSubmit) {
												e.preventDefault()
												e.stopPropagation()
												void onSubmitPress()
											}
										}}
									/>
									<button
										type="button"
										className="absolute inset-y-0 right-4 flex items-center rounded-lg p-1 text-[#424655] transition-colors hover:text-[#1a1c1f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004bc3]/30"
										onClick={() => setShowPassword(!showPassword)}
										aria-label={showPassword ? "Hide password" : "Show password"}
									>
										{showPassword ? <EyeOff className="h-6 w-6" strokeWidth={2} /> : <Eye className="h-6 w-6" strokeWidth={2} />}
									</button>
								</div>
							</div>

							<div className="space-y-2 px-1 [@media(max-height:700px)]:space-y-1.5">
								<div className="flex h-1 gap-1.5">
									{[0, 1, 2, 3].map((i) => (
										<div
											key={i}
											className={`h-1 flex-1 rounded-full ${i < strength.filled ? "bg-[#004bc3]" : "bg-[#e2e2e7]"}`}
										/>
									))}
								</div>
								<div className="flex items-center justify-between">
									<span className="text-[13px] font-semibold text-[#1a1c1f]">{strength.label}</span>
									<span className="text-[13px] font-medium text-[#424655]">{strength.percent}%</span>
								</div>
							</div>
						</div>
					</div>

						<div className="grid shrink-0 grid-cols-2 gap-2 pt-1 [@media(max-height:700px)]:gap-1.5 [@media(max-height:700px)]:pt-0">
							<div className="flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-[0_4px_24px_rgba(0,0,0,0.02)] [@media(max-height:700px)]:gap-1.5 [@media(max-height:700px)]:p-2.5">
								<Shield className="h-5 w-5 shrink-0 text-[#004bc3] [@media(max-height:700px)]:h-4 [@media(max-height:700px)]:w-4" strokeWidth={2} aria-hidden />
								<div className="min-w-0 space-y-0.5">
									<p className="text-[10px] font-bold uppercase tracking-widest text-[#424655] [@media(max-height:700px)]:text-[9px]">Vault</p>
									<p className="text-xs font-semibold leading-tight text-[#1a1c1f] [@media(max-height:700px)]:text-[11px]">
										Encrypted Local Storage
									</p>
								</div>
							</div>
							<div className="flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-[0_4px_24px_rgba(0,0,0,0.02)] [@media(max-height:700px)]:gap-1.5 [@media(max-height:700px)]:p-2.5">
								<RefreshCw className="h-5 w-5 shrink-0 text-[#004bc3] [@media(max-height:700px)]:h-4 [@media(max-height:700px)]:w-4" strokeWidth={2} aria-hidden />
								<div className="min-w-0 space-y-0.5">
									<p className="text-[10px] font-bold uppercase tracking-widest text-[#424655] [@media(max-height:700px)]:text-[9px]">Sync</p>
									<p className="text-xs font-semibold leading-tight text-[#1a1c1f] [@media(max-height:700px)]:text-[11px]">
										Multi-device Continuity
									</p>
								</div>
							</div>
						</div>

						<p className="line-clamp-2 shrink-0 px-1 text-center text-[11px] leading-snug text-[#424655] [@media(max-height:700px)]:text-[10px]">
							Verra is non-custodial. We cannot reset this password for you.
						</p>
					</div>
				</div>
			</main>

			<footer className="mx-auto flex w-full max-w-md shrink-0 flex-col items-center px-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 [@media(max-height:700px)]:px-5 [@media(max-height:700px)]:pt-1.5">
				<AppButton
					fullWidth
					loading={loading}
					disabled={!canSubmit}
					className={[
						"min-h-[52px] rounded-full py-3.5 text-base font-bold shadow-[0_8px_30px_rgba(21,98,240,0.3)] transition-transform active:scale-[0.98] [@media(max-height:700px)]:min-h-[48px] [@media(max-height:700px)]:py-3 [@media(max-height:700px)]:text-[15px]",
						canSubmit
							? "!bg-gradient-to-br !from-[#004bc3] !to-[#1562f0] hover:!opacity-[0.96] !text-white focus-visible:!ring-2 focus-visible:!ring-[#004bc3]/50 focus-visible:!ring-offset-2 focus-visible:!ring-offset-[#f3f3f8]"
							: "!cursor-not-allowed !bg-[#c3c6d8] !text-[#737687] !shadow-none",
					].join(" ")}
					onClick={() => void onSubmitPress()}
				>
					Create Wallet
				</AppButton>
				<div className="mt-2 max-w-md px-2 text-center text-[11px] font-normal leading-snug text-[#424655] [@media(max-height:700px)]:mt-1.5 [@media(max-height:700px)]:text-[10px]">
					By continuing, you agree to our{" "}
					<a href={BEAMIO_TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-[#004bc3] hover:underline">
						Terms of Service
					</a>{" "}
					and{" "}
					<a href={BEAMIO_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-[#004bc3] hover:underline">
						Privacy Policy
					</a>
					.
				</div>
			</footer>
		</div>
	)
})

export default CreateUsernamePinScreen
